const SUMMARY_KEYWORDS = ['要約', '要約して', 'まとめ', 'まとめて', 'サマリー', 'summary', 'summarize'];

const ANALYSIS_KEYWORDS = [
  '詳細',
  '詳しく',
  '深く',
  '分析',
  '技術的',
  'deep',
  'detail',
  'technical',
  'analyze',
];

const KEYWORDS = [...SUMMARY_KEYWORDS, ...ANALYSIS_KEYWORDS];

/**
 * Decides whether a message is asking about the attached files rather than
 * asking the assistant a question — a match diverts it to the client-side
 * summarisation path instead of the chat backend. Substring matching is
 * deliberate: Japanese has no word boundaries, so 「この資料を要約して」 has to
 * match on 要約.
 */
export function isFileSummaryRequest(message: string): boolean {
  const lower = message.toLowerCase();
  return KEYWORDS.some(
    (keyword) => message.includes(keyword) || lower.includes(keyword.toLowerCase()),
  );
}

/**
 * Whether the file picker should be showing for the text now in the composer.
 * A fresh `@` opens it, deleting every `@` closes it, and anything in between —
 * typing the file name — leaves it as it was.
 */
export function nextFileSuggestionState(value: string, isOpen: boolean): boolean {
  if (value.endsWith('@')) return true;
  if (isOpen && !value.includes('@')) return false;
  return isOpen;
}

/**
 * Slash commands matching what has been typed. Matches on the command itself,
 * the label and the mode id, so "/設計" and "/plan" both find 設計深掘り.
 * A bare "/" offers everything; anything not starting with "/" offers nothing.
 */
export function filterSlashCommands<T extends { command: string; label: string; modeId: string }>(
  commands: T[],
  value: string,
): T[] {
  if (!value.startsWith('/')) return [];

  const query = value.toLowerCase().slice(1);
  if (query === '') return commands;

  return commands.filter(
    (candidate) =>
      candidate.command.slice(1).startsWith(query) ||
      candidate.label.toLowerCase().includes(query) ||
      candidate.modeId.includes(query),
  );
}

/**
 * The slash commands actually offered right now: those matching what has been
 * typed AND whose mode is still available to this tenant.
 *
 * The popup and the Enter guard must agree: separate predicates meant filtering
 * one left the other matching a command with nothing on screen (#1214).
 */
export function visibleSlashCommands<
  T extends { command: string; label: string; modeId: string },
  M extends { id: string },
>(commands: T[], value: string, modes: M[]): { command: T; mode: M }[] {
  return filterSlashCommands(commands, value)
    .map((command) => ({ command, mode: modes.find((m) => m.id === command.modeId) }))
    .filter((entry): entry is { command: T; mode: M } => Boolean(entry.mode));
}
